import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import gsap from "gsap";
import { TextField } from "@mui/material";

import { updateExistAppointment } from "@/store/appointments/appointmentsSlice";
import { RootState } from "@/store/store";
import useUpdateEntity from "@/hooks/useUpdateEntity";
import { updatePayment } from "@/store/schedule/scheduleSlice";
import Button from "@/shared/components/Button/Button";
import { toast } from "react-toastify";
import useGetRole from "@/hooks/useGetRole";

import { inputStyles, visitMessage } from "@/utils/constants";
import { totalPriceCounter } from "@/services/API/totalPriceCounter";
import { dateFormatter } from "@/utils/dateFormatter";

import { ISearchDrawerProps } from "@/shared/interfaces/ui";

export default function AnimatedInputSumButton({
    toggleDrawer,
}: ISearchDrawerProps) {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>("");
    const containerRef = useRef(null);
    const dispatch = useDispatch();
    const { isAdmin } = useGetRole();
    const { handleUpdate } = useUpdateEntity();

    const appointmentData = useSelector(
        (state: RootState) => state.appointments.existtAppointment,
    );
    const isLocalLoading = useSelector(
        (state: RootState) => state.ui.isLocalLoading,
    );

    const appointmentId = appointmentData._id;
    const paymentHistory = appointmentData.paymentHistory;
    const services = appointmentData.services;
    const totalServiceSum = totalPriceCounter(services);
    const totalPayment = paymentHistory.reduce(
        (accum, item) => accum + Number(item.amount),
        0,
    );

    useEffect(() => {
        gsap.to(containerRef.current, {
            x: isExpanded ? -436 : 0,
            duration: 0.5,
            ease: "power2.out",
        });
    }, [isExpanded]);

    function handleInputValueChange(
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) {
        const value = e.target.value;
        const regex = /^[0-9]*$/;
        if (regex.test(value) && value.length < 7) {
            setInputValue(value);
        }
    }

    function updatelocalData() {
        const date = dateFormatter(new Date());
        const newPayment = {
            date: date,
            amount: String(inputValue),
        };
        const newPaymentHistory = [newPayment, ...paymentHistory];

        dispatch(
            updateExistAppointment({
                paymentHistory: [...newPaymentHistory],
            }),
        );
        dispatch(
            updatePayment({
                id: appointmentId,
                paymentHistory: newPaymentHistory,
            }),
        );
        toggleDrawer(false);
        setIsExpanded(false);
        setInputValue("");
    }

    function insertMoney() {
        const isAppointmentComplete =
            appointmentData?.isAppointmentComplete ?? false;
        if (isAdmin) {
            if (!isAppointmentComplete) {
                setIsExpanded(true);
            } else toast.error(visitMessage);
        } else toast.error("Зверніться до адміністартора!");
    }

    async function savePaidAmount() {
        if (inputValue.length > 0) {
            const newPayment = {
                date: dateFormatter(new Date()),
                amount: String(inputValue),
            };
            if (totalPayment + +inputValue <= totalServiceSum) {
                const newPaymentHistory = [
                    newPayment,
                    ...(paymentHistory || []),
                ];
                await handleUpdate({
                    entity: "appointments",
                    id: appointmentId ?? "",
                    data: { paymentHistory: [...newPaymentHistory] },
                    onSuccess: updatelocalData,
                });
            } else {
                toast.error("Введена сума більша за вартість наданих послуг");
            }
        }
    }

    return (
        <div className='drawer_total_input_container animated_container'>
            <div
                ref={containerRef}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 64,
                    width: 876,
                    position: "relative",
                }}
            >
                <Button
                    text={"Внести оплату"}
                    type='button'
                    variant='approved'
                    onClick={insertMoney}
                    style={{ width: "372px" }}
                />
                <div className='animated_container_input_container'>
                    {" "}
                    <TextField
                        type='text'
                        label='Введіть суму оплати'
                        sx={{
                            ...inputStyles.root,
                            width: 300,
                            height: "54px",
                            outline: "none",
                        }}
                        value={inputValue}
                        onChange={e => handleInputValueChange(e)}
                    />
                    <Button
                        text={"OK"}
                        type='button'
                        variant='approved'
                        onClick={savePaidAmount}
                        style={{ minWidth: 56, height: 56, flex: "01 54px" }}
                        isLoading={isLocalLoading}
                    />
                </div>
            </div>
        </div>
    );
}
